import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ordersApi } from '@/api/endpoints'
import { errorMessage } from '@/api/client'
import { saveBlob } from '@/lib/download'
import { dateOnly } from '@/lib/format'
import { StatusBadge } from '@/components/common/Ui'

const SHIPPABLE = ['confirmed', 'packed', 'partially_delivered']

const qty = (value) => {
  const n = Number(value)
  return Number.isFinite(n) ? n.toLocaleString('en-IN', { maximumFractionDigits: 3 }) : '—'
}

/**
 * Sending the order in parts.
 *
 * How much of each product has gone and arrived, a way to send some of it
 * now, and each shipment with its own invoice. The Dispatched button still
 * sends everything left in one go; this is for when it goes in loads.
 */
export default function ShipmentsPanel({ order, canShip, canReadInvoices }) {
  const queryClient = useQueryClient()
  const [shipping, setShipping] = useState(false)
  const [amounts, setAmounts] = useState({})
  const [reference, setReference] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  const lines = order.items.filter((i) => i.product_id != null)
  const progress = Object.fromEntries((order.item_progress ?? []).map((p) => [p.order_item_id, p]))
  const shipments = order.shipments ?? []
  const shippable = SHIPPABLE.includes(order.status)
  const pickup = order.fulfilment === 'pickup'

  const onSaved = (updated) => {
    queryClient.setQueryData(['order', String(order.id)], updated)
    queryClient.invalidateQueries({ queryKey: ['orders'] })
  }

  const ship = useMutation({
    mutationFn: () =>
      ordersApi.ship(order.id, {
        items: Object.entries(amounts)
          .filter(([, value]) => Number(value) > 0)
          .map(([id, value]) => ({ order_item_id: Number(id), quantity: value })),
        transport_reference: reference.trim() || null,
      }),
    onSuccess: (updated) => {
      onSaved(updated)
      setShipping(false)
      setAmounts({})
      setError('')
    },
    onError: (e) => setError(errorMessage(e)),
  })

  const deliver = useMutation({
    mutationFn: (shipmentId) => ordersApi.deliverShipment(order.id, shipmentId),
    onSuccess: onSaved,
    onError: (e) => setError(errorMessage(e)),
  })

  // Nothing to show: never shipped in parts, and not shippable by this user.
  if (!shipments.length && !(canShip && shippable)) return null

  const download = async (invoiceId) => {
    setBusy(true)
    try {
      const { blob, filename } = await ordersApi.invoicePdf(invoiceId)
      saveBlob(blob, filename)
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="shipments-panel">
      <div className="panel-title panel-title-row">
        Shipments
        {canShip && shippable && !shipping && (
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => {
              setAmounts({})
              setReference(order.transport_reference ?? '')
              setError('')
              setShipping(true)
            }}
          >
            Ship part of this order
          </button>
        )}
      </div>

      {error && <p className="field-error">{error}</p>}

      {/* One table: what each product has done, and — while a shipment is
          being made — how much of it goes now, in a column of its own. */}
      <form
        onSubmit={(e) => {
          e.preventDefault()
          setError('')
          ship.mutate()
        }}
      >
        <div className="loads-table-wrap">
          <table className="table shipments-progress">
            <thead>
              <tr>
                <th>Product</th>
                <th className="right">Ordered</th>
                <th className="right">Sent</th>
                <th className="right">Arrived</th>
                <th className="right">Left</th>
                {shipping && <th className="right">Send now</th>}
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => {
                const p = progress[line.id]
                const left = Number(p?.remaining ?? line.quantity)
                return (
                  <tr key={line.id}>
                    <td>{line.product_name}</td>
                    <td className="right nowrap">
                      {qty(line.quantity)} {String(line.unit).toLowerCase()}
                    </td>
                    <td className="right">{p ? qty(p.shipped) : '—'}</td>
                    <td className="right">{p ? qty(p.delivered) : '—'}</td>
                    <td className="right">{p ? qty(p.remaining) : '—'}</td>
                    {shipping && (
                      <td className="right">
                        {left > 0 ? (
                          <input
                            className="shipment-qty"
                            type="number"
                            min="0"
                            max={left}
                            step="any"
                            inputMode="decimal"
                            value={amounts[line.id] ?? ''}
                            placeholder="0"
                            aria-label={`Send now: ${line.product_name}`}
                            onChange={(e) => setAmounts((a) => ({ ...a, [line.id]: e.target.value }))}
                          />
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {shipping && (
          <div className="shipment-form">
            {!pickup && (
              <label className="field">
                <span>Reference No. for this shipment</span>
                <input value={reference} maxLength={64} onChange={(e) => setReference(e.target.value)} />
              </label>
            )}
            <p className="hint">
              Its own invoice is issued for exactly these quantities, and the customer is told on
              WhatsApp what is coming.
            </p>
            <div className="transport-edit-actions">
              <button type="button" className="btn btn-sm" onClick={() => setShipping(false)}>
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary btn-sm"
                disabled={ship.isPending || !Object.values(amounts).some((v) => Number(v) > 0)}
              >
                {ship.isPending ? 'Sending…' : 'Dispatch shipment'}
              </button>
            </div>
          </div>
        )}
      </form>

      {shipments.length > 0 && (
        <div className="shipment-list">
          {shipments.map((s) => (
            <div className="shipment-card" key={s.id}>
              <div className="shipment-head">
                <strong>Shipment {s.sequence}</strong>
                <StatusBadge status={s.status === 'delivered' ? 'delivered' : 'dispatched'} />
              </div>
              <div className="muted">
                {dateOnly(s.dispatched_at)}
                {s.transport_reference && ` · LR ${s.transport_reference}`}
                {s.delivered_at && ` · arrived ${dateOnly(s.delivered_at)}`}
              </div>
              <ul className="shipment-items">
                {s.items.map((si) => (
                  <li key={si.order_item_id}>
                    <span>{si.product_name}</span>
                    <span className="nowrap">
                      {qty(si.quantity)} {String(si.unit).toLowerCase()}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="shipment-actions">
                {s.invoice && canReadInvoices && (
                  <button type="button" className="btn btn-sm" disabled={busy} onClick={() => download(s.invoice.id)}>
                    Invoice {s.invoice.number}
                    {s.invoice.status === 'cancelled' && ' (cancelled)'}
                  </button>
                )}
                {canShip && s.status !== 'delivered' && order.status !== 'cancelled' && (
                  <button
                    type="button"
                    className="btn btn-sm btn-ghost"
                    disabled={deliver.isPending}
                    onClick={() => deliver.mutate(s.id)}
                  >
                    Mark delivered
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
